/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import retirementAssetsIllustration from "../../../static/illustration--retirement-assets.svg";
import CurrencyInput from "react-currency-input-field";
import { useAnswer } from "../../../util/form";
import { useAppStore } from "../../../store/store";

export default function RetirementGoalBalance({
  advanceScreen,
  previousScreen,
  validation,
  updateValidation,
  value,
  screen,
}) {
  const { form } = useAppStore();
  const { answer, setAnswer } = useAnswer(screen.id);
  // eslint-disable-next-line no-unused-vars
  const [estMonthlyIncome, setEstMonthlyIncome] = useState(0);

  useEffect(() => {
    if (value.length > 0) updateValidation(true);
    setEstMonthlyIncome(calcEstMonthlyIncome());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateAnswer(value) {
    // Make sure we're storing a clean number without commas or formatting
    // This ensures consistency when the API reads the value
    const cleanValue = value ? value.toString().replace(/,/g, '') : '';
    setAnswer(cleanValue);
    updateValidation(cleanValue.length > 0);
  }

  function formatMoney(num) {
    const options = {
      style: "decimal", // Other options: 'currency', 'percent', etc.
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };
    return parseFloat(num).toLocaleString("en-US", options);
  }

  function calcEstMonthlyIncome() {
    const montlyIncomeScreen = form.questions.find(
      (question) => String(question.questionId) === "2.4",
    );
    let currentMonthlyIncome = montlyIncomeScreen.answerKey;
    currentMonthlyIncome = (Number(currentMonthlyIncome) / 12) * 0.85; // Annual income, divide by 12 months, times 85%
    return formatMoney(currentMonthlyIncome);
  }

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="px-6 py-16 text-center md:px-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <img
            className="block h-auto mx-auto w-28 md:w-36"
            src={retirementAssetsIllustration}
            alt=""
          />
          <h1 className="mt-6 mb-4 text-3xl md:mb-5 md:mt-8 md:text-4xl">
            How much do you want to start your retirement with?
          </h1>
          <div className="max-w-2xl mx-auto">
            <p>
              The amount of money you’ll need in your retirement ultimately
              depends on your expenses and how long you live. The factors that
              come into play are impossible to know with certainty, especially
              if you’re just getting started.
            </p>
            <p>
              It’s a good idea to have a “goal balance” in mind as you work
              towards retirement.
            </p>
            <p>
              However, as challenging as this is, there are people and resources
              available to help give you a good estimate of what you’ll need,
              like{" "}
              <a
                href="https://investor.vanguard.com/tools-calculators/retirement-income-calculator"
                target="_blank"
                rel="noopener noreferrer"
              >
                Vanguard's Retirement Income Calculator
              </a>
              .
            </p>
          </div>
        </div>
        <form className="mt-10 md:mt-14">
          <div className="max-w-md mx-auto">
            <h2 className="mb-4 text-xl font-light md:mb-6 md:text-2xl">
              Enter your goal balance:
            </h2>
            <label
              className="block mb-1 text-sm font-bold text-gray-800 sr-only"
              htmlFor="retirementGoalBalance"
            >
              Retirement Goal Balance
            </label>
            <div className="flex w-40 mx-auto mt-2 rounded shadow-sm">
              <span className="inline-flex items-center px-4 text-gray-600 border border-r-0 rounded-l border-as-green-100">
                $
              </span>
              <CurrencyInput
                id="retirementGoalBalance"
                name="retirementGoalBalance"
                value={answer}
                required
                onValueChange={(value) => {
                  updateAnswer(value);
                }}
                decimalsLimit="2"
                aria-label="Retirement Goal Balance"
                className="w-full px-5 py-[1.125rem] mb-0 text-base text-gray-500 border-0 rounded-r ring-1 ring-inset ring-as-green-100 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-as-green-50"
              />
            </div>
            {/* Text removed as per RT Jones API integration requirements v2.0.1 */}
            <div className="flex justify-center gap-3 mt-10 md:mt-14 md:gap-5">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={previousScreen}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={Boolean(validation) === false}
                onClick={advanceScreen}
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
